import { useCallback, useReducer, useRef, useState } from 'react';

import { buildTextStyleOptions, getScaledTextDefaults } from '../config';
import { loadImageFromSource, revokeLayerUrls } from '../utils/loadImageFromFile';
import { getCenteredLayerPosition, getElementsBox } from '../utils/snap';
import { measureNaturalWidth } from '../elements/text/textLayout';
import { applyStyleToBlock, getBlockStyle } from '../elements/text/textRuns';
import { createEditorHistory, cloneDocumentSnapshot } from './editorHistory';
import {
  createImageElement,
  createShapeElement,
  createTextElement,
  createInitialState,
  duplicateElement,
  getElementObjectUrl,
} from './documentSchema';
import { fitViewportToContainer, panBy, zoomAtPoint } from './viewport';
import { buildCroppedElements } from './documentCrop';
import { mediaStudioReducer } from './reducer';

const revokeElements = (elements) => {
  revokeLayerUrls(
    (elements || []).map((el) => ({
      objectUrl: getElementObjectUrl(el),
      image: el.image,
    })),
  );
};

const getAnchoredPosition = (anchor, element, doc) => {
  const margin = Math.round(Math.min(doc.width, doc.height) * 0.05);
  const halfW = element.width / 2;
  const halfH = element.height / 2;
  const x = { left: margin + halfW, right: doc.width - margin - halfW }[anchor.x] ?? doc.width / 2;
  let y = doc.height / 2;
  if (anchor.y === 'top') {
    y = margin + halfH;
  } else if (anchor.y === 'bottom') {
    y = doc.height - margin - halfH;
  } else if (typeof anchor.y === 'number') {
    y = Math.min(doc.height - margin - halfH, Math.max(margin + halfH, doc.height * anchor.y));
  }
  return { x, y };
};

const useMediaStudioStore = () => {
  const [state, dispatch] = useReducer(mediaStudioReducer, undefined, createInitialState);
  const [, setHistoryTick] = useState(0);
  const [isApplyingCrop, setIsApplyingCrop] = useState(false);
  const elementsRef = useRef(state.elements);
  const stateRef = useRef(state);
  const historyRef = useRef(createEditorHistory());
  const loadGenerationRef = useRef(0);
  const isApplyingCropRef = useRef(false);
  const gestureArmedRef = useRef(false);

  elementsRef.current = state.elements;
  stateRef.current = state;

  const getSnapshot = useCallback(() => cloneDocumentSnapshot(stateRef.current), []);

  const pushHistory = useCallback(() => {
    historyRef.current.push(getSnapshot());
    setHistoryTick((value) => value + 1);
  }, [getSnapshot]);

  const bumpHistoryFlags = useCallback(() => {
    setHistoryTick((value) => value + 1);
  }, []);

  const endGesture = useCallback(() => {
    gestureArmedRef.current = false;
  }, []);

  const beginGesture = useCallback(() => {
    if (gestureArmedRef.current) {
      return;
    }
    pushHistory();
    gestureArmedRef.current = true;
  }, [pushHistory]);

  const transformElement = useCallback(
    (ids, getChanges) => {
      const targets = new Set([].concat(ids));
      const updates = stateRef.current.elements
        .filter((element) => targets.has(element.id) && !element.locked)
        .map((element) => ({ id: element.id, changes: getChanges(element) }));
      if (!updates.length) {
        return;
      }
      pushHistory();
      dispatch({ type: 'UPDATE_ELEMENTS', payload: updates });
    },
    [pushHistory],
  );

  const loadBaseImage = useCallback(
    async (source, containerSize) => {
      const generation = loadGenerationRef.current + 1;
      loadGenerationRef.current = generation;

      let loaded;
      try {
        loaded = await loadImageFromSource(source);
      } catch (error) {
        if (generation !== loadGenerationRef.current) {
          return false;
        }
        throw error;
      }

      if (generation !== loadGenerationRef.current) {
        revokeElements([{ objectUrl: loaded.objectUrl, image: loaded.image }]);
        return false;
      }

      const element = createImageElement(loaded, {
        name: loaded.name,
        x: loaded.naturalWidth / 2,
        y: loaded.naturalHeight / 2,
        width: loaded.naturalWidth,
        height: loaded.naturalHeight,
        locked: true,
      });

      const previous = elementsRef.current;
      historyRef.current.clear([]);
      const viewport = containerSize
        ? fitViewportToContainer(loaded.naturalWidth, loaded.naturalHeight, containerSize.width, containerSize.height)
        : { zoom: 1, x: 0, y: 0 };

      dispatch({
        type: 'SET_DOCUMENT_FROM_IMAGE',
        payload: {
          element,
          width: loaded.naturalWidth,
          height: loaded.naturalHeight,
          viewport,
        },
      });
      historyRef.current.pruneUnreferenced([element], previous);
      endGesture();
      bumpHistoryFlags();
      return true;
    },
    [bumpHistoryFlags, endGesture],
  );

  const addImage = useCallback(
    async (source, { name, getPlacement } = {}) => {
      if (!stateRef.current.hasDocument) {
        return loadBaseImage(source);
      }

      const generation = loadGenerationRef.current;
      let loaded;
      try {
        loaded = await loadImageFromSource(source);
      } catch (error) {
        if (generation !== loadGenerationRef.current) {
          return false;
        }
        throw error;
      }

      if (generation !== loadGenerationRef.current || !stateRef.current.hasDocument) {
        revokeElements([{ objectUrl: loaded.objectUrl, image: loaded.image }]);
        return false;
      }

      const current = stateRef.current;
      const natural = { width: loaded.naturalWidth, height: loaded.naturalHeight };
      const maxSize = Math.min(current.width, current.height) * 0.35;
      const scale = Math.min(1, maxSize / Math.max(natural.width, natural.height));
      const placement = getPlacement?.(current, natural) || {
        x: current.width / 2,
        y: current.height / 2,
        width: natural.width * scale,
        height: natural.height * scale,
      };

      const element = createImageElement(loaded, {
        name: name || source?.name || loaded.name,
        ...placement,
      });

      pushHistory();
      dispatch({ type: 'ADD_ELEMENT', payload: element });
      return true;
    },
    [loadBaseImage, pushHistory],
  );

  const addText = useCallback(
    (options = {}) => {
      const current = stateRef.current;
      if (!current.hasDocument) {
        return null;
      }

      const { preset, ...rest } = options;
      const scaled = getScaledTextDefaults(current.width, current.height, preset?.sizeRatio);
      const elementOptions = {
        ...(preset ? buildTextStyleOptions(preset, scaled.fontSize) : {}),
        ...rest,
        width: rest.width ?? (preset?.widthRatio ? current.width * preset.widthRatio : scaled.width),
        fontSize: rest.fontSize ?? scaled.fontSize,
        x: rest.x ?? current.width / 2,
        y: rest.y ?? current.height / 2,
      };
      let element = createTextElement(elementOptions);
      if (preset?.fitWidth && rest.width == null) {
        const width = Math.min(measureNaturalWidth(element), current.width * 0.9);
        element = createTextElement({ ...elementOptions, width });
      }
      if (preset?.anchor && rest.x == null && rest.y == null) {
        element = { ...element, ...getAnchoredPosition(preset.anchor, element, current) };
      }

      pushHistory();
      dispatch({ type: 'ADD_ELEMENT', payload: element });
      return element.id;
    },
    [pushHistory],
  );

  const applyTextLook = useCallback(
    (ids, look) => {
      transformElement(ids, (element) => {
        const { fontSize } = getBlockStyle(element);
        const { align, fill, bold, italic, letterSpacing, shadow, outline, background } = buildTextStyleOptions(
          look,
          fontSize,
        );
        return {
          ...applyStyleToBlock(element, { fill, bold, italic: italic ?? false }),
          ...(align ? { align } : {}),
          letterSpacing: letterSpacing ?? 0,
          shadow: shadow || { enabled: false },
          outline: outline || { enabled: false },
          background: background || { enabled: false, blur: 0 },
        };
      });
    },
    [transformElement],
  );

  const addShape = useCallback(
    (preset, name) => {
      const current = stateRef.current;
      if (!current.hasDocument) {
        return null;
      }
      const element = createShapeElement({ name, ...preset.build(current) });
      pushHistory();
      dispatch({ type: 'ADD_ELEMENT', payload: element });
      return element.id;
    },
    [pushHistory],
  );

  const updateElement = useCallback((id, changes) => {
    dispatch({ type: 'UPDATE_ELEMENT', payload: { id, changes } });
  }, []);

  const change = useCallback(
    (id, changes, { live = false } = {}) => {
      if (live) {
        beginGesture();
      } else {
        endGesture();
        pushHistory();
      }
      dispatch({ type: 'UPDATE_ELEMENT', payload: { id, changes } });
    },
    [beginGesture, endGesture, pushHistory],
  );

  const changeElements = useCallback(
    (updates, { live = false } = {}) => {
      const locked = new Set(
        stateRef.current.elements.filter((element) => element.locked).map((element) => element.id),
      );
      const unlocked = updates.filter(({ id }) => !locked.has(id));
      if (!unlocked.length) {
        return;
      }
      if (live) {
        beginGesture();
      } else {
        endGesture();
        pushHistory();
      }
      dispatch({ type: 'UPDATE_ELEMENTS', payload: unlocked });
    },
    [beginGesture, endGesture, pushHistory],
  );

  const selectElements = useCallback(
    (ids, primaryId) => {
      endGesture();
      dispatch({ type: 'SELECT_ELEMENTS', payload: { ids, primaryId } });
    },
    [endGesture],
  );

  const selectElement = useCallback(
    (id) => {
      if (id !== stateRef.current.selectedId) {
        endGesture();
      }
      dispatch({ type: 'SELECT_ELEMENT', payload: id });
    },
    [endGesture],
  );

  const toggleSelection = useCallback(
    (id) => {
      const { elements, selectedIds, selectedId } = stateRef.current;
      const element = elements.find((item) => item.id === id);
      if (!element || element.locked) {
        selectElement(id);
        return;
      }
      const unlocked = new Set(elements.filter((item) => !item.locked).map((item) => item.id));
      const base = selectedIds.filter((selected) => unlocked.has(selected));
      const ids = base.includes(id) ? base.filter((selected) => selected !== id) : [...base, id];
      selectElements(ids, base.includes(id) ? selectedId : id);
    },
    [selectElement, selectElements],
  );

  const reorderElements = useCallback(
    (elements) => {
      pushHistory();
      dispatch({ type: 'REORDER_ELEMENTS', payload: elements });
    },
    [pushHistory],
  );

  const deleteElements = useCallback(
    (ids) => {
      const targets = new Set(ids);
      const removable = stateRef.current.elements
        .filter((element) => targets.has(element.id) && !element.locked)
        .map((element) => element.id);
      if (!removable.length) {
        return;
      }
      pushHistory();
      dispatch({ type: 'DELETE_ELEMENTS', payload: removable });
    },
    [pushHistory],
  );

  const duplicateElements = useCallback(
    (ids) => {
      const current = stateRef.current;
      const targets = new Set(ids);
      const offset = Math.max(10, Math.round(Math.min(current.width, current.height) * 0.03));
      const copies = [];
      const elements = current.elements.flatMap((element) => {
        if (!targets.has(element.id) || element.locked) {
          return [element];
        }
        const copy = duplicateElement(element, { dx: offset, dy: offset });
        copies.push(copy.id);
        return [element, copy];
      });
      if (!copies.length) {
        return;
      }
      pushHistory();
      dispatch({ type: 'INSERT_ELEMENTS', payload: { elements, ids: copies } });
    },
    [pushHistory],
  );

  const deleteElement = useCallback(
    (id) => {
      const element = stateRef.current.elements.find((item) => item.id === id);
      if (!element || element.locked) {
        return;
      }
      pushHistory();
      dispatch({ type: 'DELETE_ELEMENT', payload: id });
    },
    [pushHistory],
  );

  const toggleVisibility = useCallback(
    (id) => {
      pushHistory();
      dispatch({ type: 'TOGGLE_VISIBILITY', payload: id });
    },
    [pushHistory],
  );

  const setTool = useCallback((tool) => {
    dispatch({ type: 'SET_TOOL', payload: tool });
  }, []);

  const startFormatCrop = useCallback((ratio, formatId, outputSize = null) => {
    dispatch({
      type: 'START_FORMAT_CROP',
      payload: { ratio, formatId: formatId || ratio, outputSize },
    });
  }, []);

  const setCropRect = useCallback((cropRect) => {
    dispatch({ type: 'SET_CROP_RECT', payload: cropRect });
  }, []);

  const applyCrop = useCallback(async () => {
    const current = stateRef.current;
    if (!current.cropRect || !current.hasDocument || isApplyingCropRef.current) {
      return false;
    }

    isApplyingCropRef.current = true;
    setIsApplyingCrop(true);

    const generation = loadGenerationRef.current;
    const cropRect = current.cropRect;
    const snapshot = cloneDocumentSnapshot(current);
    let created = [];

    try {
      const result = await buildCroppedElements(current.elements, cropRect);
      created = result.created;

      if (generation !== loadGenerationRef.current) {
        revokeElements(created);
        return false;
      }

      historyRef.current.push(snapshot);
      bumpHistoryFlags();
      dispatch({
        type: 'APPLY_DOCUMENT_CROP',
        payload: {
          width: Math.max(1, Math.round(cropRect.width)),
          height: Math.max(1, Math.round(cropRect.height)),
          elements: result.nextElements,
        },
      });
      return true;
    } catch (error) {
      revokeElements(created);
      throw error;
    } finally {
      isApplyingCropRef.current = false;
      setIsApplyingCrop(false);
    }
  }, [bumpHistoryFlags]);

  const flipHorizontal = useCallback(
    (id) => transformElement(id, (element) => ({ scaleX: element.scaleX * -1 })),
    [transformElement],
  );

  const flipVertical = useCallback(
    (id) => transformElement(id, (element) => ({ scaleY: element.scaleY * -1 })),
    [transformElement],
  );

  const rotateBy = useCallback(
    (id, degrees) => transformElement(id, (element) => ({ rotation: element.rotation + degrees })),
    [transformElement],
  );

  const alignElement = useCallback(
    (ids, mode) => {
      const current = stateRef.current;
      const targets = new Set([].concat(ids));
      const box = getElementsBox(current.elements.filter((element) => targets.has(element.id) && !element.locked));
      if (!box) {
        return;
      }
      const center = getCenteredLayerPosition(box, current.width, current.height, mode);
      transformElement(ids, (element) => ({ x: element.x + center.x - box.x, y: element.y + center.y - box.y }));
    },
    [transformElement],
  );

  const setViewport = useCallback((partial) => {
    dispatch({ type: 'SET_VIEWPORT', payload: partial });
  }, []);

  const zoomAt = useCallback((nextZoom, pointX, pointY) => {
    const viewport = zoomAtPoint(stateRef.current.viewport, nextZoom, pointX, pointY);
    dispatch({ type: 'SET_VIEWPORT', payload: viewport });
  }, []);

  const panViewport = useCallback((dx, dy) => {
    dispatch({ type: 'SET_VIEWPORT', payload: panBy(stateRef.current.viewport, dx, dy) });
  }, []);

  const fitToScreen = useCallback((containerSize) => {
    const current = stateRef.current;
    if (!current.width || !current.height || !containerSize?.width) {
      return;
    }
    const viewport = fitViewportToContainer(current.width, current.height, containerSize.width, containerSize.height);
    dispatch({ type: 'SET_VIEWPORT', payload: viewport });
  }, []);

  const setUi = useCallback((partial) => {
    dispatch({ type: 'SET_UI', payload: partial });
  }, []);

  const reset = useCallback(() => {
    loadGenerationRef.current += 1;
    const previous = elementsRef.current;
    historyRef.current.clear([]);
    revokeElements(previous);
    endGesture();
    dispatch({ type: 'RESET' });
    bumpHistoryFlags();
  }, [bumpHistoryFlags, endGesture]);

  const undo = useCallback(() => {
    const snapshot = historyRef.current.undo(getSnapshot());
    if (!snapshot) {
      return;
    }
    endGesture();
    dispatch({ type: 'RESTORE_SNAPSHOT', payload: snapshot });
    bumpHistoryFlags();
  }, [bumpHistoryFlags, endGesture, getSnapshot]);

  const redo = useCallback(() => {
    const snapshot = historyRef.current.redo(getSnapshot());
    if (!snapshot) {
      return;
    }
    endGesture();
    dispatch({ type: 'RESTORE_SNAPSHOT', payload: snapshot });
    bumpHistoryFlags();
  }, [bumpHistoryFlags, endGesture, getSnapshot]);

  const dispose = useCallback(() => {
    loadGenerationRef.current += 1;
    const current = elementsRef.current;
    historyRef.current.clear([]);
    endGesture();
    revokeElements(current);
  }, [endGesture]);

  return {
    ...state,
    canUndo: historyRef.current.canUndo(),
    canRedo: historyRef.current.canRedo(),
    isApplyingCrop,
    endGesture,
    loadBaseImage,
    addImage,
    addText,
    applyTextLook,
    addShape,
    updateElement,
    change,
    changeElements,
    selectElement,
    selectElements,
    toggleSelection,
    reorderElements,
    deleteElement,
    deleteElements,
    duplicateElements,
    toggleVisibility,
    setTool,
    startFormatCrop,
    setCropRect,
    applyCrop,
    reset,
    undo,
    redo,
    dispose,
    flipHorizontal,
    flipVertical,
    rotateBy,
    alignElement,
    setViewport,
    zoomAt,
    panViewport,
    fitToScreen,
    setUi,
  };
};

export default useMediaStudioStore;
